import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Api } from '../api/api';
import {
  dashboardProjectsControllerListProjectsV1,
  DashboardProjectsControllerListProjectsV1$Params,
} from '../api/functions';
import { ProjectResponseDashboardDto } from '../api/models/project-response-dashboard-dto';

/**
 * Service to manage project data across the dashboard application.
 * Provides centralized project state management.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private readonly api = inject(Api);

  // All projects for sidenav
  public readonly allProjects = signal<ProjectResponseDashboardDto[]>([]);
  public readonly allProjectsLoading = signal<boolean>(false);

  constructor() {}

  /**
   * Load all projects (without pagination).
   * Used by sidenav to populate menu items.
   */
  async loadAllProjects(): Promise<ProjectResponseDashboardDto[]> {
    this.allProjectsLoading.set(true);

    try {
      const response = await this.api.invoke(dashboardProjectsControllerListProjectsV1, {
        page: 1,
        limit: 1000, // Large limit to get all projects
      });

      const projects = response.projects || [];
      this.allProjects.set(projects);
      return projects;
    } catch (error) {
      console.error('Failed to load all projects:', error);
      this.allProjects.set([]);
      return [];
    } finally {
      this.allProjectsLoading.set(false);
    }
  }

  /**
   * Load paginated and filtered projects.
   * Used by the projects list page for display with search/pagination.
   */
  async loadProjectsPaginated(params: DashboardProjectsControllerListProjectsV1$Params): Promise<{
    projects: ProjectResponseDashboardDto[];
    total: number;
  }> {
    try {
      const response = await this.api.invoke(dashboardProjectsControllerListProjectsV1, params);
      return {
        projects: response.projects || [],
        total: response.total || 0,
      };
    } catch (error) {
      console.error('Failed to load paginated projects:', error);
      return {
        projects: [],
        total: 0,
      };
    }
  }

  /**
   * Get a single project by ID from the loaded projects.
   * Returns undefined if not found.
   */
  getProjectById(id: string): ProjectResponseDashboardDto | undefined {
    return this.allProjects().find((p) => p.id === id);
  }
}
